import { useCallback, useEffect, useState } from 'react'
import { fetchCards, fetchSets } from '@/services/cardService'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2 } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import type { FullPokemonCard, Locale } from '@/types/pokemon'
import type { Set } from '@/types/cardCatalog'
import { CardDatabaseCardsTable } from './CardDatabaseCardsTable'
import { CardDatabaseSetsTable } from './CardDatabaseSetsTable'

const PAGE_SIZE = 50

export function CardDatabaseTab() {
  const [view, setView] = useState<'sets' | 'cards'>('sets')
  const [searchInput, setSearchInput] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [localeFilter, setLocaleFilter] = useState<Locale | 'all'>('all')
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [setsData, setSetsData] = useState<{ total: number; items: Set[] } | null>(null)
  const [cardsData, setCardsData] = useState<{ total: number; items: FullPokemonCard[] } | null>(null)

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(searchInput.trim()), 350)
    return () => window.clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    setOffset(0)
  }, [debouncedQ, view, localeFilter])

  const fetchPage = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      if (view === 'sets') {
        const json = await fetchSets({
          limit: PAGE_SIZE,
          offset,
          q: debouncedQ || undefined,
          locale: localeFilter === 'all' ? undefined : localeFilter,
        })
        setSetsData({ total: json.total, items: json.items })
        setCardsData(null)
      } else {
        const json = await fetchCards({
          limit: PAGE_SIZE,
          offset,
          q: debouncedQ || undefined,
          locale: localeFilter === 'all' ? undefined : localeFilter,
        })
        setCardsData({ total: json.total, items: json.items })
        setSetsData(null)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load data')
      setSetsData(null)
      setCardsData(null)
    } finally {
      setLoading(false)
    }
  }, [view, debouncedQ, offset, localeFilter])

  useEffect(() => {
    void fetchPage()
  }, [fetchPage])

  const total = view === 'sets' ? (setsData?.total ?? 0) : (cardsData?.total ?? 0)
  const canPrev = offset > 0
  const canNext = offset + PAGE_SIZE < total

  return (
    <div className="p-4 md:p-0 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Card catalog (database)</CardTitle>
          <CardDescription>
            Sets and cards synced into the app database. Filter by locale; search matches names, codes, and series (sets) or names, collector numbers, and set fields (cards).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
              <Button type="button" variant={view === 'sets' ? 'default' : 'ghost'} size="sm" onClick={() => setView('sets')} className="rounded-md">
                Sets
              </Button>
              <Button type="button" variant={view === 'cards' ? 'default' : 'ghost'} size="sm" onClick={() => setView('cards')} className="rounded-md">
                Cards
              </Button>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end flex-1 sm:justify-end">
              <div className="space-y-1.5 w-full sm:w-[180px]">
                <Label htmlFor="locale-filter" className="text-xs text-muted-foreground">
                  Locale
                </Label>
                <Select value={localeFilter} onValueChange={(v) => setLocaleFilter(v as Locale | 'all')}>
                  <SelectTrigger id="locale-filter">
                    <SelectValue placeholder="Locale" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="de">German</SelectItem>
                    <SelectItem value="ja">Japanese</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input
                placeholder={view === 'sets' ? 'Search sets…' : 'Search cards…'}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="max-w-md w-full"
              />
            </div>
          </div>

          {!loading && total > 0 && (
            <p className="text-sm text-muted-foreground">
              Showing {view === 'sets' ? (setsData?.items.length ?? 0) : (cardsData?.items.length ?? 0)} of {total} {view}
            </p>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          )}

          {!loading && view === 'sets' && setsData && (
            <CardDatabaseSetsTable
              items={setsData.items}
              onSetsUpdated={(newSet) => {
                setSetsData((prev) => (prev ? { ...prev, items: prev.items.map((set) => (set.id === newSet.id ? newSet : set)) } : null))
              }}
            />
          )}

          {!loading && view === 'cards' && cardsData && <CardDatabaseCardsTable items={cardsData.items} />}

          {!loading && total > PAGE_SIZE && (
            <div className="flex items-center justify-between gap-2 pt-2">
              <Button type="button" variant="outline" size="sm" disabled={!canPrev} onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}>
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                {total === 0 ? '0' : `${offset + 1}–${Math.min(offset + PAGE_SIZE, total)}`} of {total}
              </span>
              <Button type="button" variant="outline" size="sm" disabled={!canNext} onClick={() => setOffset((o) => o + PAGE_SIZE)}>
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
